import { HydrateOption } from '@core/enums';
import withWidget from '@hoc/withWidget';
import * as React from 'react';
import { MultiStepFormStyles as S } from './MultiStepForm.styles';
import { Grid } from '@helpers/grid';
import ContactSection from './ContactSection/ContactSection';
import { withMotion } from '@hoc/withMotion';
import Textbox from '@stories/Components/Forms/Textbox/Textbox';
import TextArea from '@stories/Components/Forms/TextArea/TextArea';
import Button from '@stories/Components/Buttons/Button/Button';
import Select from '@stories/Components/Forms/Select/Select';
import Checkbox from '@stories/Components/Forms/Checkbox/Checkbox';
import Recaptcha from '@stories/Components/Forms/Recaptcha/Recaptcha';
const MultiStepForm = (props) => {
    const [activeFormPageIndex, setActiveFormPageIndex] = React.useState(0);
    const [overallFormState, setOverallFormState] = React.useState({});
    const [overallFormFileState, setOverallFormFileState] = React.useState({});
    const [formState, setFormState] = React.useState({});
    const [fileFormState, setFileFormState] = React.useState({});
    const contactSectionRef = React.useRef(null);
    const [contactSectionHeight, setContactSectionHeight] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [response, setResponse] = React.useState(null);
    const [message, setMessage] = React.useState(null);
    const formWrapperRef = React.useRef(null);
    const buttonBranding = props.theme === 'tpa' ? 'primary' : 'secondary';
    const coerceField = (formParams, state, key, field) => {
        if (field.type === 'dropdownlist') {
            formParams[key] = state[key].id;
            return true;
        }
        if (field.type === 'checkboxlist') {
            formParams[key] = state[key].join(',');
            return true;
        }
        if (field.type === 'checkbox') {
            formParams[key] = state[key].toString();
            return true;
        }
        return false;
    };
    const handleSubmit = async () => {
        setLoading(true);
        const formData = new FormData();
        const formParams = {};
        // If there's only one page in the form, we won't have 'overallFormState' populated yet, so we need to do it here //
        if (props.formPages.length === 1) {
            for (const key in formState) {
                const field = props.formPages[activeFormPageIndex].formFields.find((f) => f.id === key);
                if (field) {
                    if (coerceField(formParams, formState, key, field)) {
                        continue;
                    }
                }
                formParams[key] = formState[key];
            }
            if (Object.keys(fileFormState).length > 0) {
                for (const key in fileFormState) {
                    formData.append(key, fileFormState[key]);
                }
            }
        }
        else {
            overallFormState[activeFormPageIndex] = formState;
            overallFormFileState[activeFormPageIndex] = fileFormState;
            for (let index = 0; index < props.formPages.length; index++) {
                for (const key in overallFormState[index]) {
                    const field = props.formPages[index].formFields.find((f) => f.id === key);
                    if (field) {
                        if (coerceField(formParams, overallFormState[index], key, field)) {
                            continue;
                        }
                    }
                    formParams[key] = overallFormState[index][key];
                }
                if (Object.keys(overallFormFileState).length > 0) {
                    for (const key in overallFormFileState[index]) {
                        formData.append(key, overallFormFileState[index][key]);
                    }
                }
            }
        }
        formData.append('params', JSON.stringify({
            page: activeFormPageIndex,
            formName: props.formName,
            formFields: formParams,
        }));
        try {
            const response = await fetch('/api/custom-form/submit', {
                method: 'POST',
                body: formData,
            }).then((x) => x.json());
            setResponse(response);
            if (response.success) {
                if (response.redirectUrl) {
                    window.location.href = response.redirectUrl;
                    return;
                }
                if (formWrapperRef.current) {
                    setTimeout(() => {
                        formWrapperRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                }
                setMessage(response.message);
            }
        }
        finally {
            setLoading(false);
        }
    };
    const handlePrev = () => {
        if (overallFormState[activeFormPageIndex - 1]) {
            setFormState(overallFormState[activeFormPageIndex - 1]);
        }
        if (overallFormFileState[activeFormPageIndex - 1]) {
            setFileFormState(overallFormFileState[activeFormPageIndex - 1]);
        }
        setActiveFormPageIndex(activeFormPageIndex - 1);
    };
    const handleNext = async () => {
        setLoading(true);
        const formData = new FormData();
        const formParams = {};
        for (const key in formState) {
            const field = props.formPages[activeFormPageIndex].formFields.find((f) => f.id === key);
            if (field) {
                if (coerceField(formParams, formState, key, field)) {
                    continue;
                }
            }
            formParams[key] = formState[key];
        }
        formData.append('params', JSON.stringify({
            page: activeFormPageIndex,
            formName: props.formName,
            formFields: formParams,
        }));
        if (Object.keys(fileFormState).length > 0) {
            for (const key in fileFormState) {
                formData.append(key, fileFormState[key]);
            }
        }
        try {
            const response = await fetch('/api/custom-form/validate', {
                method: 'POST',
                body: formData,
            }).then((x) => x.json());
            setResponse(response);
            if (response.success && activeFormPageIndex !== props.formPages.length - 1) {
                // Collect the form data for this page, store in the overall form state, and wipe the state ready for the next page //
                setOverallFormState((prevState) => ({ ...prevState, [activeFormPageIndex]: formState }));
                setFormState({});
                setOverallFormFileState((prevState) => ({
                    ...prevState,
                    [activeFormPageIndex]: fileFormState,
                }));
                setFileFormState({});
                setActiveFormPageIndex(activeFormPageIndex + 1);
            }
        }
        finally {
            setLoading(false);
        }
    };
    const activeFormPage = React.useMemo(() => {
        return props.formPages[activeFormPageIndex];
    }, [activeFormPageIndex, props.formPages]);
    const handleFormChange = (key, value) => {
        setFormState((prevState) => ({ ...prevState, [key]: value }));
    };
    const handleFormFileChange = (key, value) => {
        if (value && value.length > 0) {
            setFileFormState((prevState) => ({ ...prevState, [key]: value[0] }));
        }
        else {
            const clone = { ...fileFormState };
            delete clone[key];
            setFileFormState(clone);
        }
    };
    React.useLayoutEffect(() => {
        if (contactSectionRef.current) {
            setContactSectionHeight(contactSectionRef.current.clientHeight);
        }
    }, [contactSectionRef]);
    const renderFormField = React.useCallback((field) => {
        switch (field.type) {
            case 'calendar':
                return (React.createElement(Textbox, { value: formState[field.id], placeholder: field.placeholder, required: field.required, label: field.label, type: "date", onChange: (e) => handleFormChange(field.id, e.currentTarget.value), disabled: loading }));
            case 'textbox':
            case 'emailinput':
            case 'number':
                return (React.createElement(Textbox, { value: formState[field.id], placeholder: field.placeholder, required: field.required, label: field.label, type: field.type, onChange: (e) => handleFormChange(field.id, e.currentTarget.value), disabled: loading }));
            case 'textarea':
                return (React.createElement(TextArea, { value: formState[field.id], placeholder: field.placeholder, required: field.required, label: field.label, onChange: (e) => handleFormChange(field.id, e.currentTarget.value), disabled: loading }));
            case 'dropdownlist':
                return (React.createElement(Select, { selectedOption: formState[field.id]?.value, label: field.label, onClick: (i) => handleFormChange(field.id, field.options[i]), options: field.options ?? [] }));
            case 'checkboxlist':
                return (React.createElement(S.ChecklistWrapper, null,
                    React.createElement("label", null,
                        field.label,
                        field.required ? ' *' : ''),
                    React.createElement(S.Checklist, null, field.options?.map((option) => (React.createElement(Checkbox, { key: `MultiStepForm_${field.id}_CheckboxList_${option.id}`, label: option.value, disabled: loading, value: formState[field.id] ? formState[field.id].includes(option.id) : false, onClick: (v) => {
                            const clone = [...(formState[field.id] ?? [])];
                            if (v) {
                                clone.push(option.id);
                            }
                            else {
                                const index = clone.indexOf(option.id);
                                clone.splice(index, 1);
                            }
                            handleFormChange(field.id, clone);
                        } }))))));
            case 'radiobuttons':
                return (React.createElement(S.ChecklistWrapper, null,
                    React.createElement("label", null,
                        field.label,
                        field.required ? ' *' : ''),
                    React.createElement(S.Checklist, null, field.options?.map((option) => (React.createElement(Checkbox, { key: `MultiStepForm_${field.id}_RadioButtons_${option.id}`, label: option.value, disabled: loading, value: formState[field.id] ? formState[field.id] === option.id : false, onClick: () => {
                            const clone = { ...formState };
                            if (formState[field.id] === option.id) {
                                delete clone[field.id];
                                setFormState(clone);
                            }
                            else {
                                handleFormChange(field.id, option.id);
                            }
                        } }))))));
            case 'checkbox':
                return (React.createElement(S.CheckboxWrapper, null,
                    React.createElement(Checkbox, { label: field.label ?? '', value: formState[field.id], disabled: loading, onClick: (v) => {
                            handleFormChange(field.id, v);
                        } }),
                    field.smallText && (React.createElement(S.SmallText, { dangerouslySetInnerHTML: { __html: field.smallText } }))));
            case 'upload':
                return (React.createElement(S.FileUpload, null,
                    field.label && React.createElement("label", null, field.label),
                    React.createElement("input", { type: "file", placeholder: field.placeholder, disabled: loading, onChange: (e) => handleFormFileChange(field.id, e.currentTarget.files) }),
                    fileFormState[field.id] && (React.createElement("small", null,
                        "You have uploaded: ",
                        fileFormState[field.id].name)),
                    field.smallText && React.createElement(S.SmallText, null, field.smallText)));
            case 'recaptcha':
                return (React.createElement(Recaptcha, { shouldLoad: true, value: formState[field.id], onChange: (value) => handleFormChange(field.id, value), siteKey: props.recaptchaSiteKey }));
            default:
                return React.createElement(React.Fragment, null);
        }
    }, [props.formPages, formState]);
    return (React.createElement(S.Wrapper, { "data-theme": props.theme, "data-contact-section-height": contactSectionHeight },
        React.createElement(Grid.Default, null,
            React.createElement(S.Container, null,
                props.contactSection && (React.createElement("div", { style: { height: 'max-content' }, ref: contactSectionRef },
                    React.createElement(ContactSection, { ...props.contactSection }))),
                React.createElement(S.FormWrapper, { ref: formWrapperRef },
                    props.formPages.length > 1 && (React.createElement(S.FormProgressWrapper, { "data-page-count": props.formPages.length }, props.formPages.map((_, index) => (React.createElement(S.FormProgressTab, { "data-active": activeFormPageIndex >= index, key: `MultiStepForm_ProgressTab_${index}` },
                        React.createElement(S.FormProgressTabActive, null)))))),
                    activeFormPage.pageName && !message && (React.createElement(S.FormPageTitle, null, activeFormPage.pageName)),
                    React.createElement(S.FormLayout, null, message ? (React.createElement(S.Message, null, message)) : (React.createElement(React.Fragment, null,
                        activeFormPage.formFields.map((field) => (React.createElement(React.Fragment, null,
                            React.createElement(S.FormField, { "data-is-checkbox": field.type === 'checkbox', "data-half-width": field.isHalfWidth, key: `MultiStepForm_FormField_${field.id}` }, renderFormField(field)),
                            response && response.errors && response.errors[field.id] && (React.createElement(S.ErrorLabel, null, response.errors[field.id]))))),
                        React.createElement(S.ButtonWrapper, null,
                            activeFormPageIndex === props.formPages.length - 1 &&
                                response &&
                                response.errors &&
                                Object.keys(response.errors).length > 0 && (React.createElement(React.Fragment, null,
                                React.createElement(S.ErrorHeading, null, "Error Summary"),
                                Object.keys(response.errors).map((key, index) => (React.createElement(S.ErrorLabel, { key: `MultiStepForm_ErrorSummary_${index}` }, response.errors[key]))))),
                            activeFormPageIndex > 0 && (React.createElement(Button, { onClick: () => handlePrev(), branding: buttonBranding, title: "Previous", disabled: loading })),
                            activeFormPageIndex < props.formPages.length - 1 && (React.createElement(Button, { onClick: () => handleNext(), branding: buttonBranding, title: "Next", disabled: loading })),
                            activeFormPageIndex === props.formPages.length - 1 && (React.createElement(Button, { onClick: () => handleSubmit(), branding: buttonBranding, title: "Submit", disabled: loading })))))))))));
};
export default withWidget(withMotion(MultiStepForm), 'MultiStepForm', {
    hydrate: HydrateOption.InView,
});
